/**
 * Pastel color palette used to visually identify team members across the app
 * (calendar events, avatar chips, list dots, etc).
 *
 * When a team member has no explicit `color`, a stable fallback is picked
 * from this palette by their position in the list.
 */
export const PASTEL_PALETTE: readonly string[] = [
    '#FFB5BA', // rose
    '#FFD3A5', // peach
    '#FFE7A0', // butter
    '#C7F0BD', // mint
    '#B5E5E0', // aqua
    '#BBD0FF', // sky
    '#D4B5FF', // lavender
    '#FFC0E5', // cotton candy
    '#E3D5CA', // sand
    '#C9C9C9', // gray
];

/** Pick a palette color by stable index (cycles modulo palette length). */
export const colorForIndex = (i: number): string => {
    const n = PASTEL_PALETTE.length;
    return PASTEL_PALETTE[((i % n) + n) % n];
};

/**
 * Returns the color to render for a given team member: their explicit
 * `color` if set, else a stable fallback from the palette based on
 * `indexInList`. Use the position the team member occupies in the
 * sorted/filtered list shown to the user so colors stay consistent.
 */
export const resolveTeamMemberColor = (
    tm: { color?: string | null } | null | undefined,
    indexInList: number
): string => {
    const c = tm?.color?.trim();
    return c && c.length > 0 ? c : colorForIndex(indexInList);
};

/**
 * Returns a high-contrast foreground color (#000 or #fff) for text drawn on
 * top of `bgHex`. Useful for keeping initials legible on top of pastel
 * backgrounds while still working if the user picks a dark custom color.
 */
export const readableTextColor = (bgHex: string): string => {
    const hex = bgHex.replace('#', '');
    if (hex.length !== 6) return '#000';
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    // YIQ luma — anything brighter than ~150 gets dark text.
    const luma = (r * 299 + g * 587 + b * 114) / 1000;
    return luma >= 150 ? '#000' : '#fff';
};
